import { useCallback } from 'react'
import { PiMicrophone, PiSpinner, PiStopCircle } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/ThemeContext'
import { formatDictationDuration } from '../../lib/voice'
import { useVoiceDictation } from '../../hooks/useVoiceDictation'
import { useToast } from '../ui/use-toast'

interface VoiceDictationButtonProps {
  compact?: boolean
  disabled?: boolean
  onTranscript: (transcript: string) => void
}

const getVoiceErrorMessage = (error: Error) => {
  switch (error.message) {
    case 'voice_permission_denied':
      return 'voice.permissionDenied'
    case 'voice_unsupported':
      return 'voice.unsupported'
    case 'voice_auth_required':
      return 'voice.authRequired'
    case 'voice_transcription_empty':
      return 'voice.emptyResult'
    case 'voice_recording_failed':
      return 'voice.recordingFailed'
    default:
      return 'voice.transcriptionFailed'
  }
}

export function VoiceDictationButton({
  compact = false,
  disabled = false,
  onTranscript
}: VoiceDictationButtonProps) {
  const { colors } = useTheme()
  const { t, i18n } = useTranslation()
  const { toast } = useToast()

  const handleError = useCallback((error: Error) => {
    toast({
      title: t('voice.errorTitle'),
      description: t(getVoiceErrorMessage(error)),
      variant: 'destructive'
    })
  }, [t, toast])

  const { elapsedSeconds, isRecording, isSupported, isTranscribing, toggleRecording } = useVoiceDictation({
    language: i18n.resolvedLanguage || i18n.language,
    onError: handleError,
    onTranscript
  })

  const buttonDisabled = disabled || isTranscribing || !isSupported
  const recordingLabel = t('voice.recording', { duration: formatDictationDuration(elapsedSeconds) })
  const buttonLabel = isTranscribing
    ? t('voice.transcribing')
    : isRecording
      ? recordingLabel
      : t('voice.start')

  const Icon = isTranscribing ? PiSpinner : isRecording ? PiStopCircle : PiMicrophone

  return (
    <button
      type="button"
      onClick={() => void toggleRecording()}
      disabled={buttonDisabled}
      aria-label={buttonLabel}
      title={!isSupported ? t('voice.unsupported') : buttonLabel}
      className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        compact ? 'text-sm' : ''
      }`}
      style={{
        backgroundColor: isRecording ? `${colors.error}10` : colors.surfaceAlt || colors.surface,
        borderColor: isRecording ? colors.error : colors.border,
        color: isRecording ? colors.error : colors.text
      }}
    >
      <Icon className={`h-4 w-4 ${isTranscribing ? 'animate-spin' : ''}`} />
      {!compact && <span>{buttonLabel}</span>}
    </button>
  )
}
